import { X, ExternalLink, MoreVertical, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StackNotification } from '@/hooks/useNotificationStack';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface NotificationCardProps {
  notification: StackNotification;
  onDismiss: (id: string) => void;
  onMarkAsRead: (id: string) => void;
  onView?: (notification: StackNotification) => void;
}

export const NotificationCard = ({
  notification,
  onDismiss,
  onMarkAsRead,
  onView,
}: NotificationCardProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }

    if (onView) {
      onView(notification);
    } else {
      // Default navigation based on notification type
      if (notification.type === 'support_message' && notification.data?.thread_id) {
        navigate(`/support/${notification.data.thread_id}`);
      } else if (notification.type === 'business_metric' && notification.data?.metric_id) {
        navigate('/business-metrics');
      }
    }
  };

  const getTimeAgo = () => {
    try {
      return formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true });
    } catch {
      return 'Just now';
    }
  };

  const renderIcon = () => {
    if (notification.icon) {
      // Check if it's a URL (starts with http:// or https:// or /)
      if (notification.icon.startsWith('http://') || 
          notification.icon.startsWith('https://') || 
          notification.icon.startsWith('/')) {
        return (
          <img 
            src={notification.icon} 
            alt="Notification icon" 
            className="w-6 h-6 object-contain rounded"
            onError={(e) => {
              // Fallback to emoji if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = document.createElement('span');
              fallback.textContent = '🔔';
              fallback.className = 'text-2xl';
              target.parentNode?.appendChild(fallback);
            }}
          />
        );
      }
      // If it's not a URL, treat it as an emoji or text
      return <span className="text-2xl">{notification.icon}</span>;
    }
    
    let emoji = '🔔';
    switch (notification.type) {
      case 'support_message':
        emoji = '💬';
        break;
      case 'business_metric':
        emoji = '📊';
        break;
    }
    return <span className="text-2xl">{emoji}</span>;
  };

  return (
    <div
      className={`
        relative bg-white dark:bg-gray-800 rounded-lg shadow-lg border
        ${notification.read ? 'border-gray-200 dark:border-gray-700' : 'border-blue-500 dark:border-blue-400'}
        p-4 mb-3 cursor-pointer hover:shadow-xl transition-all duration-200
        ${!notification.read ? 'ring-2 ring-blue-500/20' : ''}
      `}
      onClick={handleClick}
    >
      {/* Unread indicator */}
      {!notification.read && (
        <div className="absolute top-4 right-4 w-2 h-2 bg-blue-500 rounded-full" />
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-6 h-6">
            {renderIcon()}
          </div>
          <div>
            <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
              {notification.title}
            </h4>
            {notification.source && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                From {notification.source}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!notification.read && (
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsRead(notification.id);
                }}>
                  <Check className="mr-2 h-4 w-4" />
                  Mark as read
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(notification.id);
                }}
                className="text-red-600"
              >
                <X className="mr-2 h-4 w-4" />
                Dismiss
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(notification.id);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Message */}
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 line-clamp-2">
        {notification.message}
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>{getTimeAgo()}</span>
        <div className="flex items-center gap-1">
          <ExternalLink className="h-3 w-3" />
          <span>View</span>
        </div>
      </div>
    </div>
  );
};

